import React, {
    createContext,
    use,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ContextType,
    type FC,
    type PropsWithChildren,
} from "react";
import { type AccountMaps, KontoContext } from "@domain/context/accountingData/KontoEntitiesProvider";
import { CompanyContext, YearPeriodContext } from "@domain/context/CompanyContext";
import { GQL } from "@binale-tech/shared";
import { getPKRecordsRef } from "@domain/api/firebase/firebaseRootRefs";
import { type GenericRecord } from "@domain/models/GenericRecord";
import { child } from "firebase/database";
import { Subject } from "rxjs";
import { useEnrichAccounts } from "@domain/context/accountingData/hooks/useEnrichAccounts";
import { type GroupedEntry, useGroupedCollectionListener } from "@domain/context/hooks/useGroupedCollectionListener";
import { useRecordsInitializers } from "@domain/context/accountingData/useRecordsInitializers";

export type RecordsCtxData = {
    recordsER: RecordsData;
    recordsAZ: RecordsData;
    recordsFE: RecordsData;
    recordsPOS: RecordsData;
    recordsLA: RecordsData;
    recordsDeb: RecordsData;
    recordsBank: GroupedRecordsData;
    recordsKB: GroupedRecordsData;
    allRecords: AllRecordsData;
};

type RecordsData = {
    list: GenericRecord[];
    map: Map<string, GenericRecord>;
    isLoaded: boolean;
};

export type AllRecordsData = Omit<RecordsData, "snapshot"> & {
    groupsReverseIndex: Map<string, string>;
};
type GroupedRecordsData = {
    groups: Map<string, RecordsData>;
    combinedList: GenericRecord[];
    isGroupListLoaded: boolean;
};

type UnifiedRecordsDataPayload =
    | { type: "flat"; productId: GQL.IProductKey; state: Map<string, GenericRecord>; isLoaded: boolean }
    | { type: "grouped"; productId: GQL.IProductKey; state: Map<string, Map<string, GenericRecord>>; isLoaded: boolean }
    | { type: "accounts"; accountMapsAllYears: Map<number, AccountMaps> }
    | { type: "reprocess" };

const defaultRecordsData: RecordsData = {
    list: [],
    map: new Map(),
    isLoaded: false,
};
const defaultGroupedRecordsData: GroupedRecordsData = {
    combinedList: [],
    groups: new Map(),
    isGroupListLoaded: false,
};
const defaultAllRecordsData: AllRecordsData = {
    ...defaultRecordsData,
    groupsReverseIndex: new Map(),
};

const defaultContextValue = {
    recordsER: defaultRecordsData,
    recordsAZ: defaultRecordsData,
    recordsFE: defaultRecordsData,
    recordsPOS: defaultRecordsData,
    recordsLA: defaultRecordsData,
    recordsDeb: defaultRecordsData,
    recordsBank: defaultGroupedRecordsData,
    recordsKB: defaultGroupedRecordsData,
    allRecords: defaultAllRecordsData,
};
export const getDefaultRecordsValue = () => defaultContextValue;
export const RecordsContext = createContext<RecordsCtxData>(defaultContextValue);

// Per-product contexts. Each holds a single slice so consumers reading one product
// don't re-render when other products' Firebase streams tick. The bundled
// RecordsContext above is kept untouched for backward compatibility.
export const RecordsERContext = createContext<RecordsData>(defaultRecordsData);
export const RecordsAZContext = createContext<RecordsData>(defaultRecordsData);
export const RecordsFEContext = createContext<RecordsData>(defaultRecordsData);
export const RecordsPOSContext = createContext<RecordsData>(defaultRecordsData);
export const RecordsLAContext = createContext<RecordsData>(defaultRecordsData);
export const RecordsDebContext = createContext<RecordsData>(defaultRecordsData);
export const RecordsBankContext = createContext<GroupedRecordsData>(defaultGroupedRecordsData);
export const RecordsKBContext = createContext<GroupedRecordsData>(defaultGroupedRecordsData);
export const AllRecordsContext = createContext<AllRecordsData>(defaultAllRecordsData);

export const useRecordsER = () => use(RecordsERContext);
export const useRecordsAZ = () => use(RecordsAZContext);
export const useRecordsFE = () => use(RecordsFEContext);
export const useRecordsPOS = () => use(RecordsPOSContext);
export const useRecordsLA = () => use(RecordsLAContext);
export const useRecordsDeb = () => use(RecordsDebContext);
export const useRecordsBank = () => use(RecordsBankContext);
export const useRecordsKB = () => use(RecordsKBContext);
export const useAllRecords = () => use(AllRecordsContext);

export const getAllRecordsData: (
    data: Omit<ContextType<typeof RecordsContext>, "allRecords">,
) => ContextType<typeof RecordsContext>["allRecords"] = ({
    recordsER,
    recordsAZ,
    recordsFE,
    recordsPOS,
    recordsLA,
    recordsDeb,
    recordsKB,
    recordsBank,
}) => {
    const list: GenericRecord[] = [
        ...recordsER.list,
        ...recordsAZ.list,
        ...recordsFE.list,
        ...recordsPOS.list,
        ...recordsLA.list,
        ...recordsDeb.list,
        ...recordsKB.combinedList,
        ...recordsBank.combinedList,
    ];
    const map = new Map();
    list.forEach((r) => {
        map.set(r.key, r);
    });
    const groupsReverseIndex = new Map<string, string>();
    recordsKB.groups.forEach((payload, groupKey) => {
        payload.list.forEach((record) => {
            groupsReverseIndex.set(record.key, groupKey);
        });
    });
    recordsBank.groups.forEach((payload, groupKey) => {
        payload.list.forEach((record) => {
            groupsReverseIndex.set(record.key, groupKey);
        });
    });
    return { list, map, groupsReverseIndex, isLoaded: true };
};

const kbSorter = (a: GenericRecord, b: GenericRecord): number => {
    const ay = a.date.getFullYear();
    const by = b.date.getFullYear();
    if (ay !== by) {
        return ay - by;
    }
    const ap = a.period;
    const bp = b.period;
    if (ap !== bp) {
        return ap - bp;
    }
    return (Number(a.priority) || 0) - (Number(b.priority) || 0);
};

const bankSorter = (a: GenericRecord, b: GenericRecord): number => {
    const ay = a.date.getFullYear();
    const by = b.date.getFullYear();
    if (ay !== by) {
        return ay - by;
    }
    const ap = a.period;
    const bp = b.period;
    if (ap !== bp) {
        return ap - bp;
    }
    const at = a.date.getTime();
    const bt = b.date.getTime();
    if (at !== bt) {
        return at - bt;
    }
    // Bank dates are day-granular, so everything below here is "same day". Order those by
    // brutto descending (incoming above outgoing) - that puts records sharing Datum+Brutto
    // next to each other, which is what FIBU-652 asked for. The key comparison stays as the
    // final tiebreaker so equal-amount rows keep a stable order across renders.
    const ab = b.getBrutto() - a.getBrutto();
    if (ab !== 0) {
        return ab;
    }
    return a.key.localeCompare(b.key);
};

const NULL_CMP = "///___empty___///";
const DEFAULT_RECORD_GROUP_ID = "default";

const getDefaultGroupState = (state: Map<string, Map<string, GenericRecord>>): Map<string, GenericRecord> => {
    return state.get(DEFAULT_RECORD_GROUP_ID) ?? new Map<string, GenericRecord>();
};

const buildSimpleRecordsData = (
    state: Map<string, GenericRecord>,
    isLoaded: boolean,
    enrichAccounts: (r: GenericRecord) => GenericRecord,
): RecordsData => {
    const list = Array.from(state.values())
        .filter((r) => r.year)
        .map((r) => enrichAccounts(r));
    return { list, map: new Map(list.map((r) => [r.key, r])), isLoaded };
};

const buildGroupedRecordsData = (
    state: Map<string, Map<string, GenericRecord>>,
    isLoaded: boolean,
    enrichAccounts: (r: GenericRecord) => GenericRecord,
    sorter: (a: GenericRecord, b: GenericRecord) => number,
): GroupedRecordsData => {
    const groups = new Map<string, RecordsData>();
    state.forEach((itemsMap, groupId) => {
        const list = Array.from(itemsMap.values())
            .filter((r) => r.year)
            .map((r) => enrichAccounts(r))
            .toSorted(sorter);
        groups.set(groupId, { list, map: new Map(list.map((r) => [r.key, r])), isLoaded: true });
    });
    const combinedList: GenericRecord[] = [];
    groups.forEach((g) => combinedList.push(...g.list));
    return { groups, combinedList, isGroupListLoaded: isLoaded };
};

export const RecordsProvider: FC<PropsWithChildren> = (props) => {
    const { companyGQL } = use(CompanyContext);
    const { year } = use(YearPeriodContext);
    const { accountMapsAllYears } = use(KontoContext);

    const companyId = companyGQL?.id;
    const accountingYears = useMemo(() => companyGQL?.accountingYears ?? [], [companyGQL]);
    const recordWindowYears = useMemo(() => {
        const availableYears = new Set(accountingYears);
        return [year - 1, year, year + 1].filter((y) => availableYears.has(y));
    }, [accountingYears, year]);
    const shouldSkipLoad = !companyId || !recordWindowYears.length;

    // enrichAccounts is stable most of the time (reads accountMapsAllYears via ref inside
    // useEnrichAccounts). If it does get a new identity (companyBuTimeframes / accountingYearConfigs
    // change), we update enrichAccountsRef and fire "reprocess" so the subscription re-enriches all
    // loaded records without needing to tear down and re-subscribe.
    const enrichAccounts = useEnrichAccounts();
    const enrichAccountsRef = useRef(enrichAccounts);
    const unifiedDataRef = useRef(new Subject<UnifiedRecordsDataPayload>());
    const { erInit, erAInit, feInit, posInit, laInit, debInit, bankInit, kbInit } = useRecordsInitializers();

    // ── Simple products - Firebase listeners (default group, one entry per year bucket) ───────
    // Path: {root}/{companyId}/default/{year}/{recordId}

    const simpleEntries = useMemo<GroupedEntry[]>(
        () =>
            recordWindowYears.map((y) => ({
                groupId: DEFAULT_RECORD_GROUP_ID,
                subPath: `${DEFAULT_RECORD_GROUP_ID}/${y}`,
            })),
        [recordWindowYears],
    );

    const erBaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.Er), companyId ?? NULL_CMP), [companyId]);
    const erABaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.ErA), companyId ?? NULL_CMP), [companyId]);
    const feBaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.Fe), companyId ?? NULL_CMP), [companyId]);
    const posBaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.Pos), companyId ?? NULL_CMP), [companyId]);
    const laBaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.La), companyId ?? NULL_CMP), [companyId]);
    const debBaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.Deb), companyId ?? NULL_CMP), [companyId]);
    const bankBaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.Bank), companyId ?? NULL_CMP), [companyId]);
    const kbBaseRef = useMemo(() => child(getPKRecordsRef(GQL.IProductKey.Kb), companyId ?? NULL_CMP), [companyId]);

    const rawER = useGroupedCollectionListener<GenericRecord>(erBaseRef, simpleEntries, erInit, shouldSkipLoad);
    const rawAZ = useGroupedCollectionListener<GenericRecord>(erABaseRef, simpleEntries, erAInit, shouldSkipLoad);
    const rawFE = useGroupedCollectionListener<GenericRecord>(feBaseRef, simpleEntries, feInit, shouldSkipLoad);
    const rawPOS = useGroupedCollectionListener<GenericRecord>(posBaseRef, simpleEntries, posInit, shouldSkipLoad);
    const rawLA = useGroupedCollectionListener<GenericRecord>(laBaseRef, simpleEntries, laInit, shouldSkipLoad);
    const rawDeb = useGroupedCollectionListener<GenericRecord>(debBaseRef, simpleEntries, debInit, shouldSkipLoad);

    // ── Grouped products - Firebase listeners ────────────────────────────────
    // Path: {root}/{companyId}/{groupId}/{year}/{recordId}
    // Entries: cross-product of (groupIds × accountingYears); events keyed by groupId only.

    const bankList = companyGQL?.bankList;
    const kasseList = companyGQL?.kasseList;
    const bankIds = useMemo(() => [...new Set((bankList ?? []).map((b) => b.id))], [bankList]);
    const kbIds = useMemo(() => [...new Set((kasseList ?? []).map((k) => k.id))], [kasseList]);

    const bankEntries = useMemo<GroupedEntry[]>(
        () =>
            bankIds.flatMap((bankId) => recordWindowYears.map((y) => ({ groupId: bankId, subPath: `${bankId}/${y}` }))),
        [bankIds, recordWindowYears],
    );
    const kbEntries = useMemo<GroupedEntry[]>(
        () => kbIds.flatMap((kbId) => recordWindowYears.map((y) => ({ groupId: kbId, subPath: `${kbId}/${y}` }))),
        [kbIds, recordWindowYears],
    );

    const rawBank = useGroupedCollectionListener(bankBaseRef, bankEntries, bankInit, shouldSkipLoad);
    const rawKB = useGroupedCollectionListener(kbBaseRef, kbEntries, kbInit, shouldSkipLoad);

    // ── Enriched state driven by the unified Subject ──────────────────────────

    const [recordsER, setRecordsER] = useState<RecordsData>(defaultRecordsData);
    const [recordsAZ, setRecordsAZ] = useState<RecordsData>(defaultRecordsData);
    const [recordsFE, setRecordsFE] = useState<RecordsData>(defaultRecordsData);
    const [recordsPOS, setRecordsPOS] = useState<RecordsData>(defaultRecordsData);
    const [recordsLA, setRecordsLA] = useState<RecordsData>(defaultRecordsData);
    const [recordsDeb, setRecordsDeb] = useState<RecordsData>(defaultRecordsData);
    const [recordsBank, setRecordsBank] = useState<GroupedRecordsData>(defaultGroupedRecordsData);
    const [recordsKB, setRecordsKB] = useState<GroupedRecordsData>(defaultGroupedRecordsData);

    // Subscription is defined BEFORE the forwarding effects so it is listening
    // when they fire on mount (Subject does not replay missed events).
    useEffect(() => {
        const sub = unifiedDataRef.current.subscribe((event) => {
            const enrich = enrichAccountsRef.current;
            if (event.type === "flat") {
                const data = buildSimpleRecordsData(event.state, event.isLoaded, enrich);
                switch (event.productId) {
                    case GQL.IProductKey.Er:
                        return setRecordsER(data);
                    case GQL.IProductKey.ErA:
                        return setRecordsAZ(data);
                    case GQL.IProductKey.Fe:
                        return setRecordsFE(data);
                    case GQL.IProductKey.Pos:
                        return setRecordsPOS(data);
                    case GQL.IProductKey.La:
                        return setRecordsLA(data);
                    case GQL.IProductKey.Deb:
                        return setRecordsDeb(data);
                }
            } else if (event.type === "grouped") {
                const sorter = event.productId === GQL.IProductKey.Kb ? kbSorter : bankSorter;
                const data = buildGroupedRecordsData(event.state, event.isLoaded, enrich, sorter);
                switch (event.productId) {
                    case GQL.IProductKey.Bank:
                        return setRecordsBank(data);
                    case GQL.IProductKey.Kb:
                        return setRecordsKB(data);
                }
            } else if (event.type === "accounts" || event.type === "reprocess") {
                const reprocess = (prev: RecordsData): RecordsData => {
                    prev.list.forEach(enrich);
                    return { ...prev };
                };
                setRecordsER(reprocess);
                setRecordsAZ(reprocess);
                setRecordsFE(reprocess);
                setRecordsPOS(reprocess);
                setRecordsLA(reprocess);
                setRecordsDeb(reprocess);
                setRecordsBank((prev) => {
                    prev.combinedList.forEach(enrich);
                    return { ...prev };
                });
                setRecordsKB((prev) => {
                    prev.combinedList.forEach(enrich);
                    return { ...prev };
                });
            }
        });
        return () => sub.unsubscribe();
    }, []);

    // ── Forward raw hook state into the unified Subject ───────────────────────

    useEffect(() => {
        unifiedDataRef.current.next({
            type: "flat",
            productId: GQL.IProductKey.Er,
            state: getDefaultGroupState(rawER.state),
            isLoaded: rawER.isLoaded,
        });
    }, [rawER.state, rawER.isLoaded]);
    useEffect(() => {
        unifiedDataRef.current.next({
            type: "flat",
            productId: GQL.IProductKey.ErA,
            state: getDefaultGroupState(rawAZ.state),
            isLoaded: rawAZ.isLoaded,
        });
    }, [rawAZ.state, rawAZ.isLoaded]);
    useEffect(() => {
        unifiedDataRef.current.next({
            type: "flat",
            productId: GQL.IProductKey.Fe,
            state: getDefaultGroupState(rawFE.state),
            isLoaded: rawFE.isLoaded,
        });
    }, [rawFE.state, rawFE.isLoaded]);
    useEffect(() => {
        unifiedDataRef.current.next({
            type: "flat",
            productId: GQL.IProductKey.Pos,
            state: getDefaultGroupState(rawPOS.state),
            isLoaded: rawPOS.isLoaded,
        });
    }, [rawPOS.state, rawPOS.isLoaded]);
    useEffect(() => {
        unifiedDataRef.current.next({
            type: "flat",
            productId: GQL.IProductKey.La,
            state: getDefaultGroupState(rawLA.state),
            isLoaded: rawLA.isLoaded,
        });
    }, [rawLA.state, rawLA.isLoaded]);
    useEffect(() => {
        unifiedDataRef.current.next({
            type: "flat",
            productId: GQL.IProductKey.Deb,
            state: getDefaultGroupState(rawDeb.state),
            isLoaded: rawDeb.isLoaded,
        });
    }, [rawDeb.state, rawDeb.isLoaded]);
    useEffect(() => {
        unifiedDataRef.current.next({
            type: "grouped",
            productId: GQL.IProductKey.Bank,
            state: rawBank.state,
            isLoaded: rawBank.isLoaded,
        });
    }, [rawBank.state, rawBank.isLoaded]);
    useEffect(() => {
        unifiedDataRef.current.next({
            type: "grouped",
            productId: GQL.IProductKey.Kb,
            state: rawKB.state,
            isLoaded: rawKB.isLoaded,
        });
    }, [rawKB.state, rawKB.isLoaded]);

    // accountsRef.current inside enrichAccounts is updated by useEnrichAccounts's own effect,
    // which runs before this effect (hook-call order guarantee).
    useEffect(() => {
        unifiedDataRef.current.next({ type: "accounts", accountMapsAllYears });
    }, [accountMapsAllYears]);

    // enrichAccounts identity change (companyBuTimeframes / accountingYearConfigs): update ref
    // then fire "reprocess" so the subscription re-enriches without tearing down.
    useEffect(() => {
        enrichAccountsRef.current = enrichAccounts;
        unifiedDataRef.current.next({ type: "reprocess" });
    }, [enrichAccounts]);

    // ── allRecords ────────────────────────────────────────────────────────────

    const allRecords = useMemo(
        () =>
            getAllRecordsData({
                recordsER,
                recordsAZ,
                recordsFE,
                recordsPOS,
                recordsLA,
                recordsDeb,
                recordsBank,
                recordsKB,
            }),
        [recordsER, recordsAZ, recordsFE, recordsPOS, recordsLA, recordsDeb, recordsBank, recordsKB],
    );

    const recordsData: RecordsCtxData = useMemo(
        () => ({
            recordsER,
            recordsAZ,
            recordsFE,
            recordsPOS,
            recordsLA,
            recordsDeb,
            recordsBank,
            recordsKB,
            allRecords,
        }),
        [recordsER, recordsAZ, recordsFE, recordsPOS, recordsLA, recordsDeb, recordsBank, recordsKB, allRecords],
    );

    return (
        <RecordsContext value={recordsData}>
            <RecordsERContext value={recordsER}>
                <RecordsAZContext value={recordsAZ}>
                    <RecordsFEContext value={recordsFE}>
                        <RecordsPOSContext value={recordsPOS}>
                            <RecordsLAContext value={recordsLA}>
                                <RecordsDebContext value={recordsDeb}>
                                    <RecordsBankContext value={recordsBank}>
                                        <RecordsKBContext value={recordsKB}>
                                            <AllRecordsContext value={allRecords}>{props.children}</AllRecordsContext>
                                        </RecordsKBContext>
                                    </RecordsBankContext>
                                </RecordsDebContext>
                            </RecordsLAContext>
                        </RecordsPOSContext>
                    </RecordsFEContext>
                </RecordsAZContext>
            </RecordsERContext>
        </RecordsContext>
    );
};
